import { useTranslation } from "react-i18next";
import { ScrollView } from "react-native";
import { ProgressBar, Text } from "react-native-paper";
import { useAppTheme } from "../../../useAppTheme";
import Box from "../../Box";
import QuestionMapItem, {
	type QuestionMapItemState,
} from "../../QuestionMapItem";
import TooltipWrapper from "../../TooltipWrapper";
import ProgressTimer from "./ProgressTimer";
import type { ToeicProgressBarProps } from "./types";

const LISTENING_PROGRESS_HEIGHT = 4;

const STATUS_TOOLTIP_KEYS: Partial<Record<QuestionMapItemState, string>> = {
	answered: "testDriver.progressBar.statusTooltips.answered",
	current: "testDriver.progressBar.statusTooltips.inProgress",
	skipped: "testDriver.progressBar.statusTooltips.skipped",
	notVisited: "testDriver.progressBar.statusTooltips.notSeen",
};

const ToeicProgressBar = ({
	currentQuestion,
	totalQuestions,
	questionStates,
	timeLeftLabel,
	timeLeftSeconds,
	variant,
	listeningProgress = 0,
	showTimer = true,
}: ToeicProgressBarProps) => {
	const theme = useAppTheme();
	const { t } = useTranslation();

	const resolvedTimeLeftLabel = showTimer
		? (timeLeftLabel ??
			(variant === "listening"
				? t("testDriver.progressBar.timeLeftLabel")
				: t("testDriver.progressBar.timeLeftForUnit")))
		: "";

	return (
		<Box
			flexDirection="row"
			alignItems="center"
			paddingHorizontal={24}
			paddingVertical={16}
			gap={16}
			backgroundColor={theme.colors.surface}
			style={{
				borderBottomWidth: 1,
				borderBottomColor: theme.colors.outlineVariant,
				height: 74,
			}}
		>
			<Text
				style={{
					fontSize: 13,
					color: theme.colors.onSurfaceVariant,
					fontWeight: "600",
				}}
			>
				{t("testDriver.progressBar.questionOf", {
					current: currentQuestion,
					total: totalQuestions,
				})}
			</Text>

			<Box flex={1} alignItems="stretch" justifyContent="center">
				{variant === "reading" ? (
					<ScrollView
						horizontal
						showsHorizontalScrollIndicator={false}
						contentContainerStyle={{
							flexGrow: 1,
							alignItems: "center",
							justifyContent: "center",
							gap: 8,
							paddingVertical: 4,
						}}
					>
						{questionStates.map((state, i) => {
							const key = `q-${i + 1}`;
							const item = (
								<QuestionMapItem key={key} number={i + 1} state={state} />
							);
							const tooltipKey = STATUS_TOOLTIP_KEYS[state];
							return tooltipKey ? (
								<TooltipWrapper key={key} title={t(tooltipKey)}>
									{item}
								</TooltipWrapper>
							) : (
								item
							);
						})}
					</ScrollView>
				) : (
					<ProgressBar
						progress={Math.max(0, Math.min(1, listeningProgress))}
						color={theme.colors.secondary}
						style={{
							height: LISTENING_PROGRESS_HEIGHT,
							backgroundColor: theme.colors.secondaryContainer,
							borderRadius: LISTENING_PROGRESS_HEIGHT / 2,
						}}
					/>
				)}
			</Box>

			<Box
				flexDirection="row"
				alignItems="center"
				justifyContent="flex-end"
				gap={16}
			>
				{showTimer ? (
					<ProgressTimer
						label={resolvedTimeLeftLabel}
						timeLeftSeconds={timeLeftSeconds}
					/>
				) : null}
			</Box>
		</Box>
	);
};

export default ToeicProgressBar;
